/**
 * Pure utility functions for purchase logic
 * These functions have no side effects and are easily testable
 */

/**
 * Determines if the individual items section should be visible
 * @param {boolean} isCustomPurchase - Whether user selected custom purchase
 * @param {boolean} hasBundles - Whether bundles are available
 * @param {object|null} selectedBundle - Currently selected bundle
 * @param {boolean} showIndividualItems - Whether individual items are expanded
 * @param {object|null} order - Order data
 * @returns {boolean} Whether to show individual items section
 */
export const shouldShowIndividualItems = (isCustomPurchase, hasBundles, selectedBundle, showIndividualItems, order) => {
  // Don't show if no order data
  if (!order) return false;
  
  // Don't show if no items exist (FundUNow - donation only)
  if (!order.fundraiserOrderItems || order.fundraiserOrderItems.length === 0) return false;
  
  // Show if custom purchase, no bundles available, or bundle with individual items expanded
  return (isCustomPurchase || !hasBundles) || (selectedBundle && showIndividualItems);
};

/**
 * Calculates whether user has items with quantities exceeding bundle quantities
 * @param {object|null} selectedBundle - Currently selected bundle
 * @param {object|null} order - Order data with fundraiserOrderItems
 * @param {function} getBundleQuantity - Function to get bundle quantity for an option
 * @returns {boolean} Whether user has extras outside the bundle
 */
export const calculateHasExtrasOutsideBundle = (selectedBundle, order, getBundleQuantity) => {
  if (!selectedBundle || !order || !getBundleQuantity) {
    return false;
  }
  
  return order.fundraiserOrderItems?.some(item =>
    item.fundraiserOrderItemOptions.some(option => {
      const bundleQty = getBundleQuantity(option.itemOptionId);
      return option.qty > bundleQty;
    })
  ) || false;
};

/**
 * Gets the appropriate header text for the individual items section
 * @param {boolean} hasBundles - Whether bundles are available
 * @param {boolean} isCustomPurchase - Whether user selected custom purchase
 * @param {object|null} selectedBundle - Currently selected bundle
 * @param {boolean} showIndividualItems - Whether individual items are expanded
 * @returns {object} Header configuration with title and description
 */
export const getIndividualItemsHeader = (hasBundles, isCustomPurchase, selectedBundle, showIndividualItems) => {
  if (selectedBundle && showIndividualItems) {
    return {
      title: "Available Products",
      emoji: null
    };
  }
  
  if (hasBundles && isCustomPurchase) {
    return {
      title: "Available Products",
      emoji: null
    };
  }
  
  if (!hasBundles) {
    return {
      title: "Available Products",
      emoji: null
    };
  }
  
  return {
    title: "Available Products",
    emoji: null
  };
};

/**
 * Gets the appropriate description text for the individual items section
 * @param {boolean} hasExtrasOutsideBundle - Whether user has extras
 * @param {object|null} selectedBundle - Currently selected bundle
 * @param {boolean} showIndividualItems - Whether individual items are expanded
 * @param {boolean} hasBundles - Whether bundles are available
 * @param {boolean} isCustomPurchase - Whether user selected custom purchase
 * @returns {string} Description text
 */
export const getIndividualItemsDescription = (hasExtrasOutsideBundle, selectedBundle, showIndividualItems, hasBundles, isCustomPurchase) => {
  if (selectedBundle && showIndividualItems) {
    if (hasExtrasOutsideBundle) {
      return `You have additional items beyond your ${selectedBundle.name} bundle. Items with extras are shown first.`;
    } else {
      return `Add more items beyond your ${selectedBundle.name} bundle. Your bundle items are already included.`;
    }
  }
  
  if (hasBundles && isCustomPurchase) {
    return "Choose exactly what you want with individual item selection.";
  }
  
  if (!hasBundles) {
    return "Choose the items you'd like to purchase.";
  }
  
  return "Select individual items.";
};


